import {
  utils,
  apiContainer,
  RenderingServices,
  CancelablePromise,
} from '@wallaby-ui/core';
import {ColDef, ColumnApi, GridApi, SortModelItem} from 'ag-grid-community';
import {ApplyColumnStateParams} from 'ag-grid-community/dist/lib/columnController/columnApi';
import {ColGroupDef} from 'ag-grid-community/dist/lib/entities/colDef';
import {
  forOwn,
  intersectionBy,
  isArray,
  isEmpty,
  isEqual,
  isNil,
  isUndefined,
  pick,
  sortBy,
} from 'lodash';
import {MutableRefObject} from 'react';
import {IntlShape} from 'react-intl';

import {
  GRID_ACTION_SORT,
  GRID_REDUCER_STATE_ID,
  GRID_SORT_MODEL_SORT_COLUMN,
  GRID_SORT_MODEL_SORT_DIRECTION,
} from './grid_constants';
import {
  CallApiInTimeoutCallbackFunction,
  MemorizedColumnStates,
  RowsData,
} from './grid_types';
import {LIBRARY_NAME} from 'src/js/common/constants';
import {
  COLUMN_STATE_SORT,
  COLUMN_STATE_SORT_INDEX,
  ColumnConfig,
  COLUMN_AUTO_HEIGHT,
} from 'src/js/model/column_config';

const logger = utils.LogUtils.getLogger('grid_helpers', LIBRARY_NAME);

const GRID_API_CALL_CANCELED_MESSAGE =
  'Grid Api call was canceled. Probably due the unmount.';

export function getGridStateId(gridId: string): string {
  return utils.reducerUtils.getInstanceReducerId(GRID_REDUCER_STATE_ID, gridId);
}

export function getGridApiStateId(gridId: string): string {
  return utils.reducerUtils.getInstanceReducerId(
    apiContainer.API_REDUCER_STATE_ID,
    gridId
  );
}

export function getColumnStates(columnStates, columnStatesFromGridApi) {
  const COLUMN_COL_ID = 'colId';

  let result;
  if (columnStatesFromGridApi) {
    const newColumnState = {};
    columnStatesFromGridApi.forEach((columnState) => {
      newColumnState[columnState[COLUMN_COL_ID]] = pick(columnState, [
        'width',
        'hide',
      ]);
    });
    if (!isEqual(newColumnState, columnStates)) {
      result = newColumnState;
    }
  }

  return result;
}

export function getEmptyCols(rows): string[] {
  let emptyCols: string[] = [];
  if (rows.length > 0) {
    const keys = new Set(Object.keys(rows[0]));
    rows.forEach((row) => {
      keys.forEach((key) => {
        if (!isNil(row[key])) {
          keys.delete(key);
        }
      });
    });
    emptyCols = Array.from(keys);
  }

  return emptyCols;
}

export function getStateGridColumns(
  columns: ColDef[],
  isTranslated: boolean | undefined,
  styleWrapText: string,
  services: RenderingServices
): ColDef[] {
  const {navigate, intl, store, routeParams} = services;
  const newColumns = [...columns];
  let translatedColumns = newColumns;
  if (isTranslated) {
    translatedColumns = newColumns.map((column) =>
      ColumnConfig.applyServices(column, {navigate, intl, store, routeParams})
    );
  }
  let gridColumns = translatedColumns;
  if (ColumnConfig.hasAutoHeightColumn(translatedColumns)) {
    gridColumns = translatedColumns.map((column) =>
      column[COLUMN_AUTO_HEIGHT]
        ? {...column, cellClass: styleWrapText}
        : column
    );
  }
  return gridColumns;
}

export function getStateFromRows(rows: RowsData | undefined, gridOptions) {
  let hiddenColumns: string[] = [];
  const rowData = isArray(rows) || !rows?.data ? rows : rows.data;
  if (rowData && rowData.length > 0 && gridOptions.hideEmptyColumns) {
    hiddenColumns = getEmptyCols(rowData);
    // rowData = AgGrid.removeEmptyCols(rowData, hiddenColumns);
  }
  return {
    gridRows: rowData,
    ...{
      pinnedBottomRows:
        isArray(rows) || !rows?.pinnedBottomData
          ? undefined
          : rows.pinnedBottomData,
    },
    ...{
      pinnedTopRows:
        isArray(rows) || !rows?.pinnedTopData ? undefined : rows.pinnedTopData,
    },
    hiddenColumns,
  };
}

function isColGroupDef(column: ColDef | ColGroupDef): column is ColGroupDef {
  return (column as ColGroupDef).children !== undefined;
}

export function getGridHeaderLevelsCount(
  nodes: (ColDef | ColGroupDef)[]
): number {
  let columnLevels;
  if (nodes) {
    // gets number of header levels for each column
    columnLevels = nodes.map(
      (node) =>
        1 + (isColGroupDef(node) ? getGridHeaderLevelsCount(node.children) : 0)
    );
  }
  // return max number
  return columnLevels ? Math.max(...columnLevels) : 0;
}

export function enhanceGridSortWithAdditionalSort(
  currentSort: SortModelItem[],
  enhancingSortModel?: SortModelItem[]
): SortModelItem[] {
  let newSortModel = currentSort;
  if (enhancingSortModel) {
    const hasIntersection =
      intersectionBy(currentSort, enhancingSortModel, 'colId').length > 0;

    if (!hasIntersection && !isEmpty(currentSort)) {
      newSortModel = [...currentSort, ...enhancingSortModel];
    }
  }

  return newSortModel;
}

export function applyDefaultSortModel(
  currentSort: SortModelItem[] | undefined,
  defaultSort: SortModelItem[]
): SortModelItem[] | [] {
  let newSortModel = currentSort;

  if (isEmpty(currentSort)) {
    newSortModel = defaultSort;
  }

  return newSortModel ?? [];
}

export function removeDefaultSortModel(
  sortModel: SortModelItem[],
  defaultSort: SortModelItem[]
): SortModelItem[] {
  return isEqual(defaultSort, sortModel) ? [] : sortModel;
}

export function mapSortModelToColumnStateUpdate(
  sortModel: SortModelItem[]
): ApplyColumnStateParams {
  const columnStateUpdate: ApplyColumnStateParams = {
    state: [],
    defaultState: {
      // reset sort of all columns
      sort: null,
    },
  };
  if (sortModel) {
    columnStateUpdate.state = sortModel.map((item, idx) => ({
      colId: item.colId,
      sort: item.sort,
      sortIndex: idx,
    }));
  }
  return columnStateUpdate;
}

const STATIC_EMPTY_SCROLL = [0, 0];

export function setSortModelAndScroll(
  setSortModel: (
    sortModel: SortModelItem[],
    columnApi?: GridApi | ColumnApi
  ) => void,
  currentSort: SortModelItem[] | undefined,
  gridOptions: Record<string, any>,
  scrollToTopOnSort: boolean,
  isScrollCacheEnabled: () => boolean,
  setScrollPosition: (scrollPosition: any) => void,
  columnApi: MutableRefObject<ColumnApi | undefined>,
  updatePerformedAction: (
    performedActionType: any,
    remove?: any,
    reset?: any
  ) => void,
  changeRouteLoading: (up?: any, reset?: any) => void
) {
  setSortModel(
    applyDefaultSortModel(currentSort, gridOptions?.sortModel),
    columnApi.current
  );
  if (scrollToTopOnSort && isScrollCacheEnabled()) {
    // reset scroll position
    setScrollPosition(STATIC_EMPTY_SCROLL);
  }
  updatePerformedAction(GRID_ACTION_SORT);
  changeRouteLoading();
}

export function getGridColumnStatePromise(
  columnApi: ColumnApi | undefined,
  cancelablePromises: MutableRefObject<utils.CancelablePromise<any>[]>,
  callApiInTimeoutCallback: CallApiInTimeoutCallbackFunction
): Promise<SortModelItem[]> {
  const cancelablePromise = utils.PromiseUtils.makeCancelable(
    callApiInTimeoutCallback('getColumnState', undefined, columnApi)
  );
  cancelablePromises.current.push(cancelablePromise);
  return cancelablePromise.promise.then((newColumnState) => {
    const sortModel: SortModelItem[] = sortBy(
      // get only columns that are used in current sort
      newColumnState.filter((item) => item[COLUMN_STATE_SORT] != null),
      // sort columns by sort index
      [COLUMN_STATE_SORT_INDEX]
    ).map(
      // map to sort model definition
      (item) => ({
        [GRID_SORT_MODEL_SORT_COLUMN]: item.colId,
        [GRID_SORT_MODEL_SORT_DIRECTION]: item.sort,
      })
    );
    return sortModel;
  });
}

/**
 * All gridApi methods are called in a timeout callback because sometimes
 * the underlying structures are not well-initialized in time of a method call.
 * It handles issues like the React invariant #170:
 * "Expected subtree parent to be a mounted class component."
 * @param apiMethodName Name of gridApi method.
 * @param api GridApi or ColumnApi.
 * @param refElement Ref element
 * @param cancelablePromises Array of cancelable promises, new created promise is pushed into this array.
 * @param param1 Optional parameter for gridApi method.
 * @param timeoutParam timeout in milliseconds.
 * @param param2 Optional second parameter for gridApi method.
 */
export function callGridApiInTimeoutCallback(
  apiMethodName: string,
  api: GridApi | ColumnApi | undefined,
  refElement: HTMLElement | null,
  cancelablePromises: CancelablePromise[],
  param1?: any,
  timeoutParam?: number,
  param2?: any
) {
  const timeout = timeoutParam || 0;
  const setTimeoutPromise = utils.PromiseUtils.makeCancelable(
    new Promise((resolve) => {
      setTimeout(resolve, timeout);
    })
  );
  cancelablePromises.push(setTimeoutPromise);
  return setTimeoutPromise.promise
    .then(() => {
      let apiCallReturnValue;
      if (api && !isEmpty(refElement)) {
        if (isUndefined(param1) && isUndefined(param2)) {
          apiCallReturnValue = api[apiMethodName]();
        } else if (isUndefined(param2)) {
          apiCallReturnValue = api[apiMethodName](param1);
        } else {
          apiCallReturnValue = api[apiMethodName](param1, param2);
        }
      }
      return apiCallReturnValue;
    })
    .catch((error) => {
      if (error && error.isCanceled) {
        // promise was canceled, report it
        logger.warn(
          `${apiMethodName}(${
            param1 ? JSON.stringify(param1, null, 2) : ''
          }): ${GRID_API_CALL_CANCELED_MESSAGE}`
        );
      } else {
        // if another error occurred rethrow it
        throw new Error(error);
      }
    });
}

export function getOverlayNoRowsTemplate(
  intl: IntlShape,
  noRowsText: string,
  gridApi: GridApi | undefined,
  gridParams,
  headerLevels: number,
  bottomPinnedRows,
  topPinnedRows
): string {
  const text = utils.TranslateUtils.translate(noRowsText, intl);

  let totalHeaderHeight = 40;
  let totalFooterHeight = 0;
  if (gridApi) {
    // compute offset needed to add to overlay component for right positioning
    // also when there are multiple header rows (groups), bottom or top pinned rows
    const {headerHeight: defaultHeaderHeight, rowHeight} =
      gridApi.getSizesForCurrentTheme();
    const headerRowHeight =
      gridParams && gridParams.headerHeight
        ? gridParams.headerHeight
        : defaultHeaderHeight;
    totalHeaderHeight = headerRowHeight;
    if (headerLevels > 1) {
      const groupHeaderRowHeight =
        gridParams && gridParams.groupHeaderHeight
          ? gridParams.groupHeaderHeight
          : headerRowHeight;
      totalHeaderHeight += (headerLevels - 1) * groupHeaderRowHeight;
    }
    if (topPinnedRows && topPinnedRows.length > 0) {
      totalHeaderHeight += topPinnedRows.length * rowHeight;
    }
    if (bottomPinnedRows && bottomPinnedRows.length > 0) {
      totalFooterHeight += bottomPinnedRows.length * rowHeight;
    }
  }

  // change position of overlay component by adding top/bottom margin
  const style = `margin-top: ${totalHeaderHeight}px; margin-bottom: ${totalFooterHeight}px; font-family: SourceSansPro-Bold; font-size: 16px; color: #121619;`;
  // render our custom noRowsOverlay component
  return `<span className='ag-overlay-no-rows-center' style='${style}'>${text}</span>`;
}

// data transformations
export function transformRows(rows, gridOptions, isDataTranslated, intl) {
  const translate = isDataTranslated && !gridOptions?.doNotTranslateByDefault;
  if (rows && (translate || gridOptions?.nullIfZero)) {
    const newRows = [...rows];
    return newRows.map((row) => {
      const newRow = {};
      forOwn(row, (value, key) => {
        if (translate && utils.TranslateUtils.hasTranslation(value, intl)) {
          newRow[key] = utils.TranslateUtils.translate(value, intl);
        } else if (gridOptions?.nullIfZero && (value === 0 || value === '0')) {
          newRow[key] = null;
        } else {
          newRow[key] = value;
        }
      });
      return newRow;
    });
  }
  return rows;
}

function extractHiddenColumNames(
  state: MemorizedColumnStates | undefined
): string[] {
  const stateHidden: string[] = [];
  if (state) {
    const stateKeys = Object.keys(state);
    stateKeys.forEach((stateItem) => {
      if (state[stateItem].hide) {
        stateHidden.push(stateItem);
      }
    });
  }
  return stateHidden;
}

export function hasColumnVisibilityChanged(
  newState: MemorizedColumnStates | undefined,
  oldState: MemorizedColumnStates | undefined
): boolean {
  const newStateHidden = extractHiddenColumNames(newState).sort();
  const oldStateHidden = extractHiddenColumNames(oldState).sort();
  return JSON.stringify(newStateHidden) !== JSON.stringify(oldStateHidden);
}
